/**
 * Turn one arbitrary customer hex into the family of colours a console needs,
 * with every text pair proved against WCAG before it reaches a stylesheet.
 *
 * WHY THIS EXISTS. Theming the admin console from the tenant row was shipped
 * once and reverted: it assigned the RAW `tenants.primary_color` to `--primary`
 * and `--ink-strong`, New Leaf's row holds green, and the whole back office
 * repainted from warm tan to green. A second near-miss mapped a tenant's accent
 * onto `--brand-dark`, which app.css uses as the page's ink — for New Leaf that
 * put pale mint body copy on a cream page at roughly 1.1:1.
 *
 * Both failures are the same mistake: a raw customer hex landed in a slot that
 * carries MEANING (ink, chrome, status) rather than IDENTITY. So the rule here
 * is not "keep the console unthemed" — the owner has asked for it themed. It is:
 *
 *   1. Identity slots take a DERIVED value, never the raw hex.
 *   2. Every derived value carries a proven contrast ratio against the frozen
 *      substrate it will actually be seen on.
 *   3. Ink, surfaces, lines and the ten `--status-*` tokens stay literal — they
 *      are the substrate every guarantee is measured against, and a status
 *      colour IS the datum (green means complete; a green tenant absorbing
 *      `--status-go` makes "your brand" and "on track" the same pixel).
 *
 * `groundOn` returning one of two FROZEN inks rather than a computed colour is
 * the structural expression of rule 3: identity owns grounds, the ink ramp owns
 * every character on screen, and there is no code path between them.
 */

import { AAA_TEXT, AA_LARGE, AA_TEXT, bestInkOn, contrastRatio } from './contrast';
import { clamp01, gamutClip, hexToOklch, isHex6, oklchToHex } from './oklch';

/**
 * The frozen console palette, mirrored from `src/app/admin.css` `:root`.
 *
 * Duplicated here rather than parsed because this module runs in a Worker with
 * no filesystem. `tests/unit/css-tokens-defined.test.ts` asserts these still
 * match the stylesheet, so the copy cannot drift silently.
 */
export interface Substrate {
  /** `--surface-canvas` — the page ground. */
  canvas: string;
  /** `--surface-card` — panels and table bodies. */
  card: string;
  /** `--ink-strong` — headings, table numerals. */
  inkStrong: string;
  /** `--ink` — body copy. */
  ink: string;
  /** `--ink-mute` — secondary labels. */
  inkMute: string;
  /** `--ink-invert` — type on a dark ground. */
  inkInvert: string;
}

export const CONSOLE_SUBSTRATE: Substrate = {
  canvas: '#faf7f1',
  card: '#ffffff',
  inkStrong: '#2b2117',
  ink: '#3a2e24',
  inkMute: '#9a8770',
  inkInvert: '#fdfaf3',
};

export interface IdentityRamp {
  /** Fill for primary buttons, active chips, progress bars. */
  ground: string;
  groundHover: string;
  groundActive: string;
  /** Type on `ground`. Always one of the substrate's two inks. */
  groundOn: string;
  /**
   * Border for a control filled with `ground`.
   *
   * Equals `ground` when the fill is already distinguishable from the page, and
   * falls back to `line` when it is not. That is what lets a PALE brand keep its
   * pale button: WCAG 1.4.11 is satisfied by a control's boundary, so a mint
   * button on a white card gets a visible edge instead of being forcibly
   * darkened into a colour the customer never chose.
   */
  groundBorder: string;
  /** Opaque tint behind a selected row, an active tab, a nav pill. */
  wash: string;
  /** Type on `wash`. Always `inkStrong`. */
  washOn: string;
  /** Identity-coloured border on a normal surface. */
  line: string;
  /** Focus ring on a normal surface. */
  focus: string;
  /** Focus ring drawn ON a filled control — sits on `ground`, not the page. */
  focusOnGround: string;
  /** The brand AS TEXT on the page: links, active labels. Never the raw hex. */
  ink: string;
  /** Decorative only — monogram grounds, empty-state art. Never sets type. */
  quiet: string;
}

/** Chroma ceilings per role. A fluorescent brand is their hue, not their headache. */
const C_GROUND = 0.16;
const C_INK = 0.14;
const C_LINE = 0.1;
const C_WASH = 0.035;
const C_QUIET = 0.06;

/**
 * Find the lightness closest to `l0`, moving only in `dir`, at which `ok`
 * holds for the emitted colour.
 *
 * Contrast against a fixed reference is monotone in lightness once you are
 * moving away from that reference, so a bisection converges. The predicate is
 * evaluated on the CLIPPED, emitted hex rather than on the ideal OKLCH value,
 * because the clipped hex is what ships.
 *
 * Returns `null` when no lightness in that direction satisfies `ok`.
 */
function solveLightness(
  l0: number,
  c: number,
  h: number,
  dir: -1 | 1,
  ok: (hex: string) => boolean,
): string | null {
  const at = (l: number): string => oklchToHex(gamutClip(clamp01(l), c, h));

  if (ok(at(l0))) return at(l0);

  const limit = dir === -1 ? 0 : 1;
  if (!ok(at(limit))) return null;

  let near = l0; // known to fail
  let far = limit; // known to pass
  /*
   * 14 halvings resolve lightness to ~6e-5. The result is rounded to an 8-bit
   * channel, so anything finer is discarded — and this loop calls `gamutClip`,
   * which bisects too, so the two costs multiply.
   */
  for (let i = 0; i < 14; i += 1) {
    const mid = (near + far) / 2;
    if (ok(at(mid))) far = mid;
    else near = mid;
  }
  return at(far);
}

/**
 * Derive the identity family for one brand colour.
 *
 * Never throws and never returns a broken screen: a malformed hex, a pure grey,
 * a fluorescent, `#000000` and `#FFFFFF` all resolve to a ramp that satisfies
 * every assertion in `ramp.test.ts`.
 */
export function deriveIdentityRamp(
  brandHex: string,
  substrate: Substrate = CONSOLE_SUBSTRATE,
): IdentityRamp {
  const { canvas, card, inkStrong, ink, inkMute, inkInvert } = substrate;
  const base = isHex6(brandHex) ? hexToOklch(brandHex) : hexToOklch('#2C2C2C');
  const { l: l0, h } = base;

  // --- ground -------------------------------------------------------------
  /*
   * Two ways to make a label legible on this colour: darken it until near-white
   * type works, or lighten it until near-black type works. Take whichever moves
   * the customer's colour LESS. Both always have a solution (L→0 and L→1 are
   * near-black and near-white), so `ground` is never the fallback branch.
   */
  const cGround = Math.min(base.c, C_GROUND);
  const darker = solveLightness(l0, cGround, h, -1, (hex) => contrastRatio(hex, inkInvert) >= AA_TEXT);
  const lighter = solveLightness(l0, cGround, h, 1, (hex) => contrastRatio(hex, inkStrong) >= AA_TEXT);

  const dl = darker === null ? Infinity : Math.abs(hexToOklch(darker).l - l0);
  const ll = lighter === null ? Infinity : Math.abs(hexToOklch(lighter).l - l0);
  // Tie goes to the darker candidate: a dark control reads as a control.
  const ground = (dl <= ll ? darker : lighter) ?? oklchToHex(gamutClip(0.35, cGround, h));
  const groundOn = bestInkOn(ground, inkStrong, inkInvert);

  // --- hover / active -----------------------------------------------------
  /*
   * Both steps move AWAY from the label's own luminance, so the label can only
   * get more readable, never less. That makes the state colours safe by
   * construction instead of by a re-check — and "darker when pressed" on a dark
   * button, "lighter when pressed" on a pale one, are both ordinary treatments.
   */
  const groundL = hexToOklch(ground).l;
  const stateDir: -1 | 1 = contrastRatio(ground, inkInvert) >= contrastRatio(ground, inkStrong) ? -1 : 1;
  const step = (delta: number): string =>
    oklchToHex(gamutClip(clamp01(groundL + stateDir * delta), cGround, h));
  const groundHover = step(0.05);
  const groundActive = step(0.09);

  /*
   * A fill only has to be distinguishable from the page when it is the only
   * thing marking the control. When it is not, the border does that job.
   */
  const groundIsDistinct =
    contrastRatio(ground, card) >= AA_LARGE && contrastRatio(ground, canvas) >= AA_LARGE;

  // --- line + focus -------------------------------------------------------
  // Both surfaces are light, so both solutions darken.
  const line =
    solveLightness(
      l0,
      Math.min(base.c, C_LINE),
      h,
      -1,
      (hex) => contrastRatio(hex, card) >= AA_LARGE && contrastRatio(hex, canvas) >= AA_LARGE,
    ) ?? oklchToHex(gamutClip(0.45, Math.min(base.c, C_LINE), h));

  const focus =
    solveLightness(
      l0,
      cGround,
      h,
      -1,
      (hex) => contrastRatio(hex, card) >= AA_LARGE && contrastRatio(hex, canvas) >= AA_LARGE,
    ) ?? line;

  // --- ink ----------------------------------------------------------------
  const identityInk =
    solveLightness(
      l0,
      Math.min(base.c, C_INK),
      h,
      -1,
      (hex) => contrastRatio(hex, card) >= AA_TEXT && contrastRatio(hex, canvas) >= AA_TEXT,
    ) ?? inkStrong;

  // --- wash ---------------------------------------------------------------
  /*
   * DO NO HARM, rather than an absolute floor, for `--ink-mute`.
   *
   * `--ink-mute` (#9a8770) on the frozen `--surface-card` is already only ~3.3:1
   * — a pre-existing gap in the console's own palette that this ramp neither
   * caused nor can fix. Demanding 4.5 here would be asserting something the
   * frozen tier itself fails. So the wash's obligation is that it never makes a
   * pairing WORSE than the page canvas already is.
   */
  const inkMuteOnCanvas = contrastRatio(inkMute, canvas);
  const cWash = Math.min(base.c * 0.18, C_WASH);
  const wash =
    solveLightness(
      0.955,
      cWash,
      h,
      1,
      (hex) =>
        contrastRatio(inkStrong, hex) >= AAA_TEXT &&
        contrastRatio(ink, hex) >= AA_TEXT &&
        contrastRatio(inkMute, hex) >= inkMuteOnCanvas,
    ) ?? canvas;

  // --- quiet --------------------------------------------------------------
  const quiet =
    solveLightness(0.9, Math.min(base.c * 0.35, C_QUIET), h, 1, (hex) =>
      contrastRatio(inkStrong, hex) >= AA_TEXT,
    ) ?? wash;

  return {
    ground,
    groundHover,
    groundActive,
    groundOn,
    groundBorder: groundIsDistinct ? ground : line,
    wash,
    washOn: inkStrong,
    line,
    focus,
    /*
     * A ring drawn on a filled control sits on `ground`, not on the page, so it
     * reuses the label's ink — already proved at ≥4.5 there, comfortably past
     * the 3:1 that 1.4.11 asks of a ring.
     */
    focusOnGround: groundOn,
    ink: identityInk,
    quiet,
  };
}
